import { useState } from "react";
import { AlertTriangle, Loader2, Plus } from "lucide-react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { type Role } from "@/lib/users.api";
import { useAuth } from "@/App";
import { useCreateUserMutation } from "@/hooks/mutations/useCreateUser";

const ASSIGNABLE_ROLES: { value: Role; label: string }[] = [
  { value: "agent", label: "Agent" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
  { value: "super_admin", label: "Super Admin" },
  { value: "consultant", label: "Consultant" },
  { value: "dev", label: "Dev / Supervision" },
];

interface CreateUserDialogProps {
  onCreated: () => void;
}

export function CreateUserDialog({ onCreated }: CreateUserDialogProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<Role>("agent");
  const [manualOtp, setManualOtp] = useState<{
    email: string;
    code: string;
    expiresAt: string;
  } | null>(null);
  const createMutation = useCreateUserMutation();

  // Only super_admin manages admin/super_admin/dev accounts (M1 spec + C2), matching the server guard.
  const availableRoles = ASSIGNABLE_ROLES.filter(
    (r) => user?.role === "super_admin" || !["admin", "super_admin", "dev"].includes(r.value),
  );

  function reset() {
    setEmail("");
    setFullName("");
    setRole("agent");
    setManualOtp(null);
  }

  function handleSubmit() {
    createMutation.mutate(
      { email, fullName, role },
      {
        onSuccess: (created) => {
          const delivery = created.otpDelivery;
          if (delivery.manualOtp) {
            setManualOtp({
              email: created.email,
              code: delivery.manualOtp,
              expiresAt: delivery.otpExpiresAt,
            });
            onCreated();
            return;
          }

          setOpen(false);
          reset();
          onCreated();
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger>
        <Button size="sm">
          <Plus size={14} /> Nouvel utilisateur
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nouvel utilisateur</DialogTitle>
          <DialogDescription>
            Un code OTP sera envoye par email pour la premiere connexion. Si les emails sont
            desactives, le code manuel apparaitra ici une seule fois.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {manualOtp && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
                <div className="space-y-1">
                  <p className="font-semibold">Email indisponible</p>
                  <p>Partagez ce code avec {manualOtp.email} pour sa premiere connexion :</p>
                  <p className="font-mono text-lg font-bold tracking-[0.2em] text-amber-950">
                    {manualOtp.code}
                  </p>
                  <p className="text-amber-700">
                    Ce code expire le {new Date(manualOtp.expiresAt).toLocaleString("fr-FR")}.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-[11.5px] font-medium text-body mb-1.5">
              Nom complet
            </label>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoFocus={!manualOtp}
              disabled={!!manualOtp}
            />
          </div>
          <div>
            <label className="block text-[11.5px] font-medium text-body mb-1.5">Email</label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              disabled={!!manualOtp}
            />
          </div>
          <div>
            <label className="block text-[11.5px] font-medium text-body mb-1.5">Role</label>
            <Select
              value={role}
              onValueChange={(v) => setRole(v as Role)}
              disabled={!!manualOtp}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            {manualOtp ? "Fermer" : "Annuler"}
          </Button>
          {!manualOtp && (
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending || !email || !fullName}
            >
              {createMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : "Creer"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
